import { useState } from 'react'
import { toast } from 'sonner'
import { Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDollars, grossUpStripeFee } from '@/lib/format'
import { track } from '@/lib/track'
import type { DetailOrganization, Event } from '@/types'
import DonationPicker, { type DonationInterval } from './DonationPicker'

type Props = {
  event: Event
  organization: DetailOrganization | null
  isPreview?: boolean
}

export default function DonateBlock({ event, organization, isPreview = false }: Props) {
  const [amountCents, setAmountCents] = useState<number | null>(null)
  const [interval, setInterval] = useState<DonationInterval>('one_time')
  const [coverFee, setCoverFee] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const isMonthly = interval === 'monthly'
  // Cover-the-fee is one-time only; monthly gifts charge the exact amount.
  const effectiveCoverFee = isMonthly ? false : coverFee
  const { feeCents, grossCents } = grossUpStripeFee(amountCents ?? 0)
  const buttonCents = amountCents == null ? null : effectiveCoverFee ? grossCents : amountCents

  async function handleDonate() {
    if (isPreview || submitting || amountCents == null) return
    setSubmitting(true)
    track(event.id, 'donation_click', { amount_cents: String(amountCents), interval })
    try {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? ''
      const res = await fetch('/checkout/donation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({
          event_id: event.id,
          amount_cents: amountCents,
          interval,
          cover_processing_fee: effectiveCoverFee,
        }),
      })
      const data = (await res.json()) as { url?: string; error?: string }
      if (data.url) {
        window.location.assign(data.url)
        return
      }
      toast.error("We couldn't start checkout. Please try again or contact the organizer.")
    } catch {
      toast.error("We couldn't start checkout. Please try again or contact the organizer.")
    }
    setSubmitting(false)
  }

  return (
    <div className="mt-4 rounded-xl border border-primary/15 bg-primary/5 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Heart className="size-4 text-primary" fill="currentColor" />
        <h3 className="text-base font-semibold">Donate</h3>
      </div>

      <DonationPicker
        amountCents={amountCents}
        onChange={setAmountCents}
        interval={interval}
        onIntervalChange={setInterval}
      />

      {amountCents != null && !isMonthly && (
        <label className="mt-3 flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2">
            <Checkbox
              checked={coverFee}
              onCheckedChange={(v) => setCoverFee(v === true)}
              aria-label="Cover processing fee"
            />
            <span className="text-foreground">Cover processing fee</span>
          </span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {formatDollars(feeCents)}
          </span>
        </label>
      )}

      <Button
        type="button"
        className="mt-3 w-full"
        onClick={handleDonate}
        disabled={isPreview || submitting || amountCents == null}
      >
        {buttonCents != null
          ? `Donate ${formatDollars(buttonCents)}${isMonthly ? ' / month' : ''}`
          : 'Donate'}
      </Button>

      <p className="mt-2 text-xs text-muted-foreground">
        {organization?.name
          ? `Your tax receipt (if applicable) will come from ${organization.name}.`
          : 'Your tax receipt (if applicable) will come from the organizer.'}
      </p>
    </div>
  )
}
